import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { updateInquiry, clearError } from "../../../store/slices/inquirySlice";
import EnquirerInfo from "./EnquiryForm/EnquirerInfo";
import ClientInfo from "./EnquiryForm/ClientInfo";
import Medications from "./EnquiryForm/Medications";
import PhysicalHealth from "./EnquiryForm/PhysicalHealth";
import RiskAssessment from "./EnquiryForm/RiskAssessment";
import AggressionHistory from "./EnquiryForm/AggressionHistory";
import ClientBehavior from "./EnquiryForm/ClientBehavior";
import TreatmentPlan from "./EnquiryForm/TreatmentPlan";

const EditPatientInquiry = ({ inquiryData }) => {
    const { id: patientId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isLoading, error } = useSelector((state) => state.inquiries);

    const [activeTab, setActiveTab] = useState("enquirer-info");
    const [formData, setFormData] = useState({
        inquiry_id: null,
        programId: "",
        programName: "",
        contactMethod: "",
        inquirerName: "",
        relationWithClient: "",
        phone1: "",
        phone2: "",
        email: "",
        address: "",
        howHeard: [],
        referredBy: { psychiatrist: "", psychologist: "", otherMedical: "" },
        otherSource: "",
        clientName: "",
        clientGender: "",
        clientEducation: "",
        clientBirthOrder: "",
        clientSiblings: "",
        clientDOB: "",
        client_cnic: "",
        client_phone: "",
        client_email: "",
        client_address: "",
        familyMentalIllness: "",
        familyMentalIllnessDetails: "",
        diagnosis: "",
        diagnosisSince: "",
        currentCare: "",
        trhServices: "",
        trhServicesDetails: "",
        substanceUse: "",
        substanceUseDetails: "",
        presentingComplaints: "",
        medications: [{ name: "", dosage: "" }],
        prescribedBy: "",
        prescriptionDate: "",
        prevServices: [],
        otherService: "",
        prevHospitalizations: [{ hospital: "", duration: "", consultant: "" }],
        medicalConditions: "",
        medicalConditionsDetails: "",
        healthIssues: [],
        otherHealthIssue: "",
        illnessHistory: "",
        expectations: "",
        suicidalThoughts: "",
        suicideFrequency: "",
        suicidePlans: "",
        suicideAttempts: "",
        suicideAttemptsDetails: "",
        desireEscape: "",
        escapeAttempts: "",
        escapeAttemptsDetails: "",
        historyAggression: "",
        historyAggressionDetails: "",
        harmToOthers: "",
        harmToOthersDetails: "",
        selfHarmHistory: "",
        selfHarmHistoryDetails: "",
        selfHarmUrges: "",
        selfHarmUrgesDetails: "",
        recommendedFor: [],
        opdDetails: "",
        followUpNotes: "",
        prev_aggression: "",
        last_episode: "",
        incident_provoked: "",
        regret_incident: "",
        show_denial: "",
        episode_description: "",
        cope_stress: "",
        client_general_behavior: "",
        impulsive_behavior: "",
        impulsive_example_1: "",
        impulsive_example_2: "",
        impulsive_example_3: "",
        symptoms: "",
        rehab_expectations: "",
        fear_aggression: "",
        psychiatrist_note: "",
        treatmentOption: "",
        reason: "",
        amount: "",
        payment_status: "",
        reference_no: "",
        payment_date: "",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Map backend inquiry -> formData (camelCase)
    const mapInquiryToFormData = (inquiry) => {
        if (!inquiry) return {};

        const master = inquiry.master || {};
        const program = inquiry.program || master.program || {};
        const clientInfo = inquiry.clientInformation || {};
        const clientMed = inquiry.clientMedications || {};
        const medsList = inquiry.medicationsList || [];
        const prevHosp = inquiry.prevHospitalizations || [];
        const history = inquiry.clientHistory || {};
        const risk = inquiry.riskForm || {};
        const hear = inquiry.hearAboutUs || [];
        const aggressionHistory = inquiry.aggressionHistory || {};
        const clientBehavior = inquiry.clientBehavior || {};
        const treatmentPlan = inquiry.treatmentPlan || {};

        const medications = medsList.length
            ? medsList.map((m) => ({ name: m.name || "", dosage: m.dosage || "" }))
            : [{ name: "", dosage: "" }];

        const howHeard = Array.isArray(hear) ? hear.map((h) => h.value) : [];

        const referredBy = {
            psychiatrist:
                hear.find((h) => (h.value || "").includes("Psychiatrist"))?.name || "",
            psychologist:
                hear.find((h) => (h.value || "").includes("Psychologist"))?.name || "",
            otherMedical:
                hear.find((h) => (h.value || "").includes("Medical Practitioner"))
                    ?.name || "",
        };

        const prevServices = clientMed.prev_services
            ? Array.isArray(clientMed.prev_services)
                ? clientMed.prev_services
                : (() => {
                    try {
                        return JSON.parse(clientMed.prev_services || "[]");
                    } catch {
                        return [];
                    }
                })()
            : [];

        const prevHospitalizations =
            prevHosp.length > 0
                ? prevHosp.map((h) => ({
                    hospital: h.hospital || "",
                    duration: h.duration || "",
                    consultant: h.consultant || "",
                }))
                : [{ hospital: "", duration: "", consultant: "" }];

        const healthIssues = history.experienced_issues
            ? Array.isArray(history.experienced_issues)
                ? history.experienced_issues
                : history.experienced_issues.split(",").map((s) => s.trim())
            : [];

        const recommendedFor = Array.isArray(risk.recommended_for)
            ? risk.recommended_for
            : typeof risk.recommended_for === "string" && risk.recommended_for.includes(",")
                ? risk.recommended_for.split(",").map((s) => s.trim())
                : risk.recommended_for
                    ? [risk.recommended_for]
                    : [];

        return {
            inquiry_id: master.id || null,
            programId: program.id || master.program_master_id || "",
            programName: program.name || "",
            contactMethod: master.contact_method || "",
            inquirerName: master.inquirer_name || "",
            relationWithClient: master.relation_with_client || "",
            phone1: master.phone1 || "",
            phone2: master.phone2 || "",
            email: master.email || "",
            address: master.address || "",
            howHeard,
            referredBy,
            otherSource: hear.find((h) => h.value === "Other")?.name || master.other_source || "",
            clientName: clientInfo.client_name || "",
            clientGender: clientInfo.client_gender || "",
            clientEducation: clientInfo.client_education || "",
            clientBirthOrder: clientInfo.client_birth_order || "",
            clientSiblings: clientInfo.client_siblings || "",
            clientDOB: clientInfo.client_dob || "",
            client_cnic: clientInfo.client_cnic || "",
            client_phone: clientInfo.client_phone || "",
            client_email: clientInfo.client_email || "",
            client_address: clientInfo.client_address || "",
            familyMentalIllness: clientInfo.family_mental_illness || "",
            familyMentalIllnessDetails: clientInfo.family_mental_illness_details || "",
            diagnosis: clientInfo.diagnosis || "",
            diagnosisSince: clientInfo.diagnosis_since || "",
            currentCare: clientInfo.current_care || "",
            presentingComplaints: clientInfo.presenting_complaints || "",
            trhServices: clientInfo.trh_services || "",
            trhServicesDetails: clientInfo.trh_services_details || "",
            substanceUse: clientInfo.substance_use || "",
            substanceUseDetails: clientInfo.substance_use_details || "",
            medications,
            prescribedBy: clientMed?.[0]?.prescribed_by || "",
            prescriptionDate: clientMed?.[0]?.prescription_date || "",
            prevServices: clientMed?.[0]?.prev_services
                ? JSON.parse(clientMed[0].prev_services)
                : [],
            otherService: clientMed?.[0]?.other_service || "",
            prevHospitalizations: prevHosp?.map((h) => ({
                hospital: h.hospital || "",
                duration: h.duration || "",
                consultant: h.consultant || "",
            })) || [{ hospital: "", duration: "", consultant: "" }],
            medicalConditions: history.medical_conditions || "",
            medicalConditionsDetails: history.medical_conditions_details || "",
            healthIssues,
            otherHealthIssue: history.other_health_issue || "",
            illnessHistory: history.illness_history || "",
            expectations: history.expectations || "",
            suicidalThoughts: risk.suicidal_thoughts || "",
            suicideFrequency: risk.frequency_suicidal_thoughts || "",
            suicidePlans: risk.current_or_past_suicide_plans || "",
            suicideAttempts: risk.previous_suicide_attempts || "",
            suicideAttemptsDetails: risk.previous_suicide_attempts_details || "",
            desireEscape: risk.desire_to_escape || "",
            escapeAttempts: risk.past_escape_attempts || "",
            escapeAttemptsDetails: risk.past_escape_attempts_details || "",
            historyAggression: risk.history_of_aggression || "",
            historyAggressionDetails: risk.history_of_aggression_details || "",
            harmToOthers: risk.current_risk_of_harm_to_others || "",
            harmToOthersDetails: risk.specific_individuals || "",
            selfHarmHistory: risk.history_of_self_harm || "",
            selfHarmHistoryDetails: risk.history_of_self_harm_details || "",
            selfHarmUrges: risk.current_self_harm_urges || "",
            selfHarmUrgesDetails: risk.specific_self_harm_actions || "",
            recommendedFor,
            opdDetails: risk.opd_details || "",
            followUpNotes: risk.follow_up_notes || "",
            prev_aggression: aggressionHistory.prev_aggression || "",
            last_episode: aggressionHistory.last_episode || "",
            incident_provoked: aggressionHistory.incident_provoked || "",
            regret_incident: aggressionHistory.regret_incident || "",
            show_denial: aggressionHistory.show_denial || "",
            episode_description: aggressionHistory.episode_description || "",
            cope_stress: aggressionHistory.cope_stress || "",
            client_general_behavior: clientBehavior.client_general_behavior || "",
            impulsive_behavior: clientBehavior.impulsive_behavior || "",
            impulsive_example_1: clientBehavior.impulsive_example_1 || "",
            impulsive_example_2: clientBehavior.impulsive_example_2 || "",
            impulsive_example_3: clientBehavior.impulsive_example_3 || "",
            symptoms: clientBehavior.symptoms || "",
            rehab_expectations: clientBehavior.rehab_expectations || "",
            fear_aggression: clientBehavior.fear_aggression || "",
            psychiatrist_note: clientBehavior.psychiatrist_note || "",
            treatmentOption: treatmentPlan.treatment_option || "",
            reason: treatmentPlan.reason || "",
            amount: master.amount || "",
            payment_status: master.payment_status || "",
            reference_no: master.reference_no || "",
            payment_date: master.payment_date || "",
        };
    };

    useEffect(() => {
        if (inquiryData) {
            const mapped = mapInquiryToFormData(inquiryData);
            setFormData((prev) => ({ ...prev, ...mapped }));
        }
    }, [inquiryData]);

    // ================= VALIDATION FUNCTIONS =================

    const validateEnquirerInfo = () => {
        const newErrors = {};
        if (!formData.contactMethod) {
            newErrors.contactMethod = "Contact method is required";
        }
        if (!formData.inquirerName?.trim()) {
            newErrors.inquirerName = "Inquirer name is required";
        }
        if (!formData.phone1?.trim()) {
            newErrors.phone1 = "Phone number is required";
        }
        if (!formData.email?.trim()) {
            newErrors.email = "Email is required";
        }
        if (!formData.relationWithClient) {
            newErrors.relationWithClient = "Relation with client is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateClientInfo = () => {
        const newErrors = {};
        if (!formData.clientName?.trim()) {
            newErrors.clientName = "Client name is required";
        }
        if (!formData.clientGender?.trim()) {
            newErrors.clientGender = "Gender is required";
        }
        if (!formData.clientDOB?.trim()) {
            newErrors.clientDOB = "Date of birth is required";
        }
        if (!formData.client_phone?.trim()) {
            newErrors.client_phone = "Phone number is required";
        }
        if (!formData.client_cnic?.trim()) {
            newErrors.client_cnic = "CNIC is required";
        }
        if (!formData.client_email?.trim()) {
            newErrors.client_email = "Email is required";
        }
        if (!formData.client_address?.trim()) {
            newErrors.client_address = "Address is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateMedications = () => {
        const newErrors = {};
        if (!formData.medications?.[0]?.name?.trim()) {
            newErrors.medications = "At least one medication is required";
        }
        if (!formData.prescribedBy?.trim()) {
            newErrors.prescribedBy = "Prescribed by is required";
        }
        if (!formData.prescriptionDate) {
            newErrors.prescriptionDate = "Prescription date is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePhysicalHealth = () => {
        const newErrors = {};
        if (!formData.medicalConditions) {
            newErrors.medicalConditions = "Please specify if there are known medical conditions";
        }
        if (formData.medicalConditions === "Yes" && !formData.medicalConditionsDetails?.trim()) {
            newErrors.medicalConditionsDetails = "Please specify the medical conditions";
        }
        if (!formData.illnessHistory?.trim()) {
            newErrors.illnessHistory = "Brief history of illness is required";
        }
        if (!formData.expectations?.trim()) {
            newErrors.expectations = "Expectations from rehabilitation services is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateRiskAssessment = () => {
        const newErrors = {};
        if (!formData.suicidalThoughts) {
            newErrors.suicidalThoughts = "Please specify if there are suicidal thoughts";
        }
        if (formData.suicidalThoughts === "Yes" && !formData.suicideFrequency) {
            newErrors.suicideFrequency = "Please specify frequency of suicidal thoughts";
        }
        if (!formData.suicidePlans) {
            newErrors.suicidePlans = "Please specify if there are current or past suicide plans";
        }
        if (!formData.suicideAttempts) {
            newErrors.suicideAttempts = "Please specify if there were previous suicide attempts";
        }
        if (formData.suicideAttempts === "Yes" && !formData.suicideAttemptsDetails?.trim()) {
            newErrors.suicideAttemptsDetails = "Please provide details of previous suicide attempts";
        }
        if (!formData.desireEscape) {
            newErrors.desireEscape = "Please specify if there is a desire to escape";
        }
        if (!formData.escapeAttempts) {
            newErrors.escapeAttempts = "Please specify if there were past escape attempts";
        }
        if (formData.escapeAttempts === "Yes" && !formData.escapeAttemptsDetails?.trim()) {
            newErrors.escapeAttemptsDetails = "Please provide details of past escape attempts";
        }
        if (!formData.historyAggression) {
            newErrors.historyAggression = "Please specify if there is a history of aggression";
        }
        if (formData.historyAggression === "Yes" && !formData.historyAggressionDetails?.trim()) {
            newErrors.historyAggressionDetails = "Please provide details of history of aggression";
        }
        if (!formData.harmToOthers) {
            newErrors.harmToOthers = "Please specify if there is a current risk of harm to others";
        }
        if (formData.harmToOthers === "Yes" && !formData.harmToOthersDetails?.trim()) {
            newErrors.harmToOthersDetails = "Please specify any specific individuals at risk";
        }
        if (!formData.selfHarmHistory) {
            newErrors.selfHarmHistory = "Please specify if there is a history of self-harm";
        }
        if (formData.selfHarmHistory === "Yes" && !formData.selfHarmHistoryDetails?.trim()) {
            newErrors.selfHarmHistoryDetails = "Please provide details of history of self-harm";
        }
        if (!formData.selfHarmUrges) {
            newErrors.selfHarmUrges = "Please specify if there are current self-harm urges";
        }
        if (formData.selfHarmUrges === "Yes" && !formData.selfHarmUrgesDetails?.trim()) {
            newErrors.selfHarmUrgesDetails = "Please specify specific actions considered";
        }
        if (formData.recommendedFor.length === 0) {
            newErrors.recommendedFor = "Please select at least one recommendation";
        }
        if (formData.recommendedFor.includes("OPD Services outside TRH") && !formData.opdDetails?.trim()) {
            newErrors.opdDetails = "Please provide details for OPD services";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateAggressionHistory = () => {
        const newErrors = {};
        if (!formData.prev_aggression) {
            newErrors.prev_aggression = "Please specify if there was a previous episode of aggression/violence";
        }
        if (formData.prev_aggression === "Yes") {
            if (!formData.last_episode?.trim()) {
                newErrors.last_episode = "Please specify when was the last episode";
            }
            if (!formData.incident_provoked) {
                newErrors.incident_provoked = "Please specify if the incident was provoked";
            }
            if (!formData.regret_incident) {
                newErrors.regret_incident = "Please specify if he/she regrets the incident";
            }
            if (!formData.show_denial) {
                newErrors.show_denial = "Please specify if he/she shows denial";
            }
            if (!formData.episode_description?.trim()) {
                newErrors.episode_description = "Please describe the episode briefly";
            }
        }
        if (!formData.cope_stress?.trim()) {
            newErrors.cope_stress = "Please describe how the client handles/copes with stress";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateClientBehavior = () => {
        const newErrors = {};
        if (!formData.client_general_behavior) {
            newErrors.client_general_behavior = "Please select the client's general behavior";
        }
        if (!formData.impulsive_behavior) {
            newErrors.impulsive_behavior = "Please specify if the client exhibits impulsive behavior";
        }
        if (formData.impulsive_behavior === "Yes") {
            if (!formData.impulsive_example_1?.trim()) {
                newErrors.impulsive_example_1 = "Please provide first example of impulsive behavior";
            }
            if (!formData.impulsive_example_2?.trim()) {
                newErrors.impulsive_example_2 = "Please provide second example of impulsive behavior";
            }
            if (!formData.impulsive_example_3?.trim()) {
                newErrors.impulsive_example_3 = "Please provide third example of impulsive behavior";
            }
        }
        if (!formData.symptoms?.trim()) {
            newErrors.symptoms = "Please describe the symptoms";
        }
        if (!formData.rehab_expectations?.trim()) {
            newErrors.rehab_expectations = "Please describe expectations from rehabilitation services";
        }
        if (!formData.fear_aggression) {
            newErrors.fear_aggression = "Please specify if you fear another episode of violence/aggression";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateTreatmentPlan = () => {
        const newErrors = {};
        if (!formData.treatmentOption) {
            newErrors.treatmentOption = "Please select a treatment plan";
        }
        if (!formData.reason?.trim()) {
            newErrors.reason = "Please provide a reason for the selected treatment plan";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ================= NEXT BUTTON HANDLERS =================

    const handleEnquirerNext = () => {
        if (validateEnquirerInfo()) {
            setActiveTab("client-info");
        }
    };

    const handleClientNext = () => {
        if (validateClientInfo()) {
            setActiveTab("medications");
        }
    };

    const handleMedicationsNext = () => {
        if (!validateMedications()) return;
        if (formData.programId == "2") {
            setActiveTab("aggression-history");
        } else {
            setActiveTab("physical-health");
        }
    };

    const handlePhysicalHealthNext = () => {
        if (validatePhysicalHealth()) {
            setActiveTab("risk-assessment");
        }
    };

    const handleAggressionHistoryNext = () => {
        if (validateAggressionHistory()) {
            setActiveTab("client-behavior");
        }
    };

    const handleClientBehaviorNext = () => {
        if (validateClientBehavior()) {
            setActiveTab("treatment-plan");
        }
    };

    // ================= FORM HANDLERS =================

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            const arr = Array.isArray(formData[name]) ? formData[name] : [];
            const updatedArray = checked ? [...arr, value] : arr.filter((item) => item !== value);
            setFormData({ ...formData, [name]: updatedArray });
            return;
        }

        if (name.includes("referredBy.")) {
            const [, subKey] = name.split(".");
            setFormData({ ...formData, referredBy: { ...formData.referredBy, [subKey]: value } });
            return;
        }

        if (name.includes("medications.") || name.includes("prevHospitalizations.")) {
            const [field, indexStr, subField] = name.split(".");
            const index = Number(indexStr);
            const arr = Array.isArray(formData[field]) ? [...formData[field]] : [];
            arr[index] = { ...(arr[index] || {}), [subField]: value };
            setFormData({ ...formData, [field]: arr });
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const addMedication = () => {
        setFormData({
            ...formData,
            medications: [...(formData.medications || []), { name: "", dosage: "" }],
        });
    };

    const removeMedication = (index) => {
        setFormData({
            ...formData,
            medications: (formData.medications || []).filter((_, i) => i !== index),
        });
    };

    const addHospitalization = () => {
        setFormData({
            ...formData,
            prevHospitalizations: [...(formData.prevHospitalizations || []), { hospital: "", duration: "", consultant: "" }],
        });
    };

    const removeHospitalization = (index) => {
        setFormData({
            ...formData,
            prevHospitalizations: (formData.prevHospitalizations || []).filter((_, i) => i !== index),
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.contactMethod) newErrors.contactMethod = "Contact method is required";
        if (!formData.inquirerName) newErrors.inquirerName = "Inquirer name is required";
        if (!formData.clientName) newErrors.clientName = "Client name is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate based on selected program
        let isValid = false;

        if (formData.programId == "2") {
            isValid = validateTreatmentPlan();
        } else {
            isValid = validateRiskAssessment();
        }

        if (!isValid) {
            Swal.fire({
                title: "Incomplete Form",
                text: "Please fill in all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        const id = formData.inquiry_id;
        if (!id) {
            Swal.fire({
                title: "Error",
                text: "No inquiry ID found to update.",
                icon: "error",
            });
            return;
        }

        const formatForApi = (data) => {
            const d = { ...data };

            if (d.suicideAttempts === "No") d.suicideAttemptsDetails = null;
            if (d.escapeAttempts === "No") d.escapeAttemptsDetails = null;
            if (d.historyAggression === "No") d.historyAggressionDetails = null;
            if (d.harmToOthers === "No") d.harmToOthersDetails = null;
            if (d.selfHarmHistory === "No") d.selfHarmHistoryDetails = null;
            if (d.selfHarmUrges === "No") d.selfHarmUrgesDetails = null;

            return {
                ...d,
                suicidal_thoughts: d.suicidalThoughts,
                frequency_suicidal_thoughts: d.suicideFrequency,
                current_or_past_suicide_plans: d.suicidePlans,
                previous_suicide_attempts: d.suicideAttempts,
                previous_suicide_attempts_details: d.suicideAttemptsDetails,
                desire_to_escape: d.desireEscape,
                past_escape_attempts: d.escapeAttempts,
                past_escape_attempts_details: d.escapeAttemptsDetails,
                history_of_aggression: d.historyAggression,
                history_of_aggression_details: d.historyAggressionDetails,
                current_risk_of_harm_to_others: d.harmToOthers,
                specific_individuals: d.harmToOthersDetails,
                history_of_self_harm: d.selfHarmHistory,
                history_of_self_harm_details: d.selfHarmHistoryDetails,
                current_self_harm_urges: d.selfHarmUrges,
                specific_self_harm_actions: d.selfHarmUrgesDetails,
                treatment_option: d.treatmentOption,
            };
        };

        try {
            const result = await dispatch(
                updateInquiry({
                    id,
                    inquiryData: {
                        ...formatForApi(formData),
                        patient_id: patientId,
                        program_master_id: formData.programId,
                    },
                })
            ).unwrap();

            await Swal.fire({
                title: "Success",
                text: "Inquiry updated successfully!",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate("/patients");
        } catch (err) {
            console.error("❌ Update failed:", err);

            const errorMessage =
                typeof err === "object"
                    ? JSON.stringify(err, null, 2)
                    : err || "Failed to update inquiry";

            Swal.fire({
                title: "Update Failed",
                text: errorMessage,
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    if (!formData.inquiry_id) return <div>Loading existing inquiry details...</div>;

    const selectedProgram = String(formData.programId);

    return (
        <div className="enquiry-form-wrapper">
            <div className="bs-stepper wizard-vertical">
                <div className="bs-stepper-header">
                    {(
                        formData.programId == 2
                            ? [
                                { key: "enquirer-info", label: "Enquirer Info", icon: "ri-contacts-line" },
                                { key: "client-info", label: "Client Info", icon: "ri-user-line" },
                                { key: "medications", label: "Medications", icon: "ri-medicine-bottle-line" },
                                { key: "aggression-history", label: "Aggression History", icon: "ri-fire-line" },
                                { key: "client-behavior", label: "Client Behavior", icon: "ri-brain-line" },
                                { key: "treatment-plan", label: "Treatment Plan", icon: "ri-file-list-3-line" },
                            ]
                            : formData.programId == 3
                                ? [
                                    { key: "enquirer-info", label: "Enquirer Info", icon: "ri-contacts-line" },
                                    { key: "client-info", label: "Client Info", icon: "ri-user-line" },
                                    { key: "medications", label: "Medications", icon: "ri-medicine-bottle-line" },
                                    { key: "physical-health", label: "Physical Health", icon: "ri-heart-line" },
                                    { key: "risk-assessment", label: "Risk Assessment", icon: "ri-shield-line" },
                                ]
                                : [
                                    { key: "enquirer-info", label: "Enquirer Info", icon: "ri-contacts-line" },
                                    { key: "client-info", label: "Client Info", icon: "ri-user-line" },
                                    { key: "medications", label: "Medications", icon: "ri-medicine-bottle-line" },
                                ]
                    ).map((tab) => (
                        <div className="step" key={tab.key}>
                            <button
                                type="button"
                                className={`step-trigger ${activeTab === tab.key ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <span className="avatar">
                                    <span className="avatar-initial rounded-2 bg-primary text-white">
                                        <i className={`${tab.icon} ri-24px`}></i>
                                    </span>
                                </span>
                                <span className="bs-stepper-label">
                                    <span className="bs-stepper-title">{tab.label}</span>
                                </span>
                            </button>
                        </div>
                    ))}
                </div>

                <div className="bs-stepper-content">
                    <div className="d-flex justify-content-end mb-4">
                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.programName || "N/A"}
                                    disabled
                                    readOnly
                                />
                                <label>Program</label>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {activeTab === "enquirer-info" && (
                            <EnquirerInfo 
                                formData={formData} 
                                handleChange={handleChange} 
                                setActiveTab={setActiveTab} 
                                selectedProgram={selectedProgram}
                                errors={errors}
                                onNext={handleEnquirerNext}
                            />
                        )}
                        {activeTab === "client-info" && (
                            <ClientInfo 
                                formData={formData} 
                                handleChange={handleChange} 
                                setActiveTab={setActiveTab} 
                                selectedProgram={selectedProgram} 
                                isAdd={false}
                                errors={errors}
                                onNext={handleClientNext}
                            />
                        )}
                        {activeTab === "medications" && (
                            <Medications
                                formData={formData}
                                handleChange={handleChange}
                                addMedication={addMedication}
                                removeMedication={removeMedication}
                                addHospitalization={addHospitalization}
                                removeHospitalization={removeHospitalization}
                                setActiveTab={setActiveTab}
                                selectedProgram={selectedProgram}
                                errors={errors}
                                onNext={handleMedicationsNext}
                            />
                        )}

                        {formData.programId == 2 && activeTab === "aggression-history" && (
                            <AggressionHistory 
                                formData={formData} 
                                handleChange={handleChange} 
                                setActiveTab={setActiveTab} 
                                selectedProgram={selectedProgram}
                                errors={errors}
                                onNext={handleAggressionHistoryNext}
                            />
                        )}
                        {formData.programId == 2 && activeTab === "client-behavior" && (
                            <ClientBehavior 
                                formData={formData} 
                                handleChange={handleChange} 
                                setActiveTab={setActiveTab} 
                                selectedProgram={selectedProgram}
                                errors={errors}
                                onNext={handleClientBehaviorNext}
                            />
                        )}
                        {formData.programId == 2 && activeTab === "treatment-plan" && (
                            <TreatmentPlan 
                                formData={formData} 
                                handleChange={handleChange} 
                                setActiveTab={setActiveTab} 
                                selectedProgram={selectedProgram}
                                errors={errors}
                            />
                        )}

                        {formData.programId == 3 && activeTab === "physical-health" && (
                            <PhysicalHealth 
                                formData={formData} 
                                handleChange={handleChange} 
                                setActiveTab={setActiveTab} 
                                selectedProgram={selectedProgram}
                                errors={errors}
                                onNext={handlePhysicalHealthNext}
                            />
                        )}
                        {formData.programId == 3 && activeTab === "risk-assessment" && (
                            <RiskAssessment 
                                formData={formData} 
                                handleChange={handleChange} 
                                setActiveTab={setActiveTab} 
                                selectedProgram={selectedProgram}
                                errors={errors}
                            />
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditPatientInquiry;